import { useState } from 'react';
import { Download, Search } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCandidates } from '@/hooks/useCandidates';
import { exportCandidatesToXlsx } from '@/lib/exportXlsx';
import { CandidateDrawer } from '@/components/Candidate/CandidateDrawer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatBRL, formatROA, isStale } from '@/lib/utils';
import type { FunnelStatus, CandidateProfile, CandidateOrigin } from '@/types';

const STATUS_OPTIONS: { value: FunnelStatus | 'all'; label: string }[] = [
  { value: 'all',            label: 'Todos os status'     },
  { value: 'prospeccao',     label: 'Prospecção'          },
  { value: 'r1',             label: 'R1 — Café'           },
  { value: 'r2_r3',          label: 'R2/R3 — Entrevista'  },
  { value: 'proposta',       label: 'Proposta'            },
  { value: 'fechado_ganho',  label: 'Fechado Ganho'       },
  { value: 'fechado_perdido','label': 'Fechado Perdido'   },
];

const PROFILE_OPTIONS: { value: CandidateProfile | 'all'; label: string }[] = [
  { value: 'all',        label: 'Todos os perfis' },
  { value: 'farmer',     label: 'Farmer'          },
  { value: 'hunter',     label: 'Hunter'          },
  { value: 'private',    label: 'Private'         },
  { value: 'alta_renda', label: 'Alta Renda'      },
];

const ORIGIN_OPTIONS: { value: CandidateOrigin | 'all'; label: string }[] = [
  { value: 'all',                label: 'Todas as origens'  },
  { value: 'linkedin',           label: 'LinkedIn'          },
  { value: 'indicacao_interna',  label: 'Indicação Interna' },
  { value: 'evento',             label: 'Evento'            },
  { value: 'ativo',              label: 'Ativo'             },
  { value: 'headhunter',         label: 'Headhunter'        },
];

function scoreColor(score: number) {
  if (score >= 70) return '#3A7A58';
  if (score >= 40) return '#B07C18';
  return '#A83830';
}

export function CandidatesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter]   = useState<FunnelStatus | 'all'>('all');
  const [profileFilter, setProfileFilter] = useState<CandidateProfile | 'all'>('all');
  const [originFilter, setOriginFilter]   = useState<CandidateOrigin | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: candidates = [], isLoading, error } = useCandidates();

  const filtered = candidates.filter((c) => {
    if (statusFilter  !== 'all' && c.funnel_status !== statusFilter)  return false;
    if (profileFilter !== 'all' && c.profile       !== profileFilter) return false;
    if (originFilter  !== 'all' && c.origin        !== originFilter)  return false;
    if (search) {
      const q = search.toLowerCase();
      if (!c.name.toLowerCase().includes(q) && !c.current_institution.toLowerCase().includes(q))
        return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col h-full">

      {/* Toolbar */}
      <div className="border-b border-border bg-bg-surface px-5 py-3 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <h1 className="text-sm font-semibold text-text-primary">Candidatos</h1>
            <span className="font-mono text-2xs text-text-muted">{filtered.length}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => exportCandidatesToXlsx(candidates)}>
            <Download className="h-3 w-3" />
            Exportar
          </Button>
        </div>

        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-44">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-text-muted" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nome ou instituição…"
              className="pl-7 h-7 text-xs"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as FunnelStatus | 'all')}>
            <SelectTrigger className="w-44 h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={profileFilter} onValueChange={(v) => setProfileFilter(v as CandidateProfile | 'all')}>
            <SelectTrigger className="w-40 h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROFILE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={originFilter} onValueChange={(v) => setOriginFilter(v as CandidateOrigin | 'all')}>
            <SelectTrigger className="w-44 h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ORIGIN_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex h-full items-center justify-center">
            <span className="text-sm text-text-muted">Carregando…</span>
          </div>
        )}
        {error && (
          <div className="flex h-full items-center justify-center">
            <span className="text-sm text-status-red">{error.message}</span>
          </div>
        )}
        {!isLoading && !error && (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-bg-surface border-b border-border">
              <tr>
                {['Nome', 'Instituição', 'Perfil', 'AUM', 'ROA', 'Status', 'Score', 'Última Interação'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-widest text-text-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const stale = isStale(c.last_interaction_date);
                return (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className={[
                      'border-b border-border cursor-pointer transition-colors hover:bg-bg-elevated',
                      stale ? 'bg-status-red/[0.03]' : '',
                    ].join(' ')}
                  >
                    <td className="px-4 py-2.5 font-medium text-text-primary">{c.name}</td>
                    <td className="px-4 py-2.5 text-text-secondary">{c.current_institution}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="secondary">{c.profile}</Badge>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-text-secondary tabular-nums">
                      {formatBRL(c.aum_cents)}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-text-secondary tabular-nums">
                      {formatROA(c.roa_bps)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="default">{c.funnel_status}</Badge>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold tabular-nums"
                      style={{ color: scoreColor(c.score) }}>
                      {c.score}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-text-muted tabular-nums">
                      {c.last_interaction_date
                        ? format(parseISO(c.last_interaction_date), 'dd/MM/yyyy', { locale: ptBR })
                        : '—'}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-text-muted">
                    Nenhum candidato encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <CandidateDrawer
        candidateId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
